/**
 * Excel Format Generator
 *
 * Generates Excel (.xlsx) files from trip data using ExcelJS
 * Provides professional formatting with proper columns, headers, and styling
 */

import ExcelJS from 'exceljs';

class ExcelFormatGenerator {
  constructor() {
    this.defaultStyles = {
      header: {
        font: { bold: true, size: 12, color: { argb: 'FFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '4472C4' } },
        alignment: { horizontal: 'center', vertical: 'middle' }
        // Removed all borders - no border lines in itinerary export
      },
      cell: {
        font: { size: 11 },
        alignment: { horizontal: 'left', vertical: 'top', wrapText: true }
        // Removed all borders - no border lines in itinerary export
      },
      title: {
        font: { bold: true, size: 16 },
        alignment: { horizontal: 'center' }
      }
    };
  }

  /**
   * Generate Excel file for itinerary data
   *
   * @param {Array} itineraryData - Structured itinerary data
   * @param {Object} metadata - Trip metadata (tripName, dateRange, dayCount)
   * @returns {Promise<Blob>} Excel file as blob
   */
  async generateItineraryExcel(itineraryData, metadata) {
    const workbook = new ExcelJS.Workbook();

    // Set workbook properties
    workbook.creator = 'Trip Planner';
    workbook.lastModifiedBy = 'Trip Planner';
    workbook.created = new Date();
    workbook.modified = new Date();
    workbook.lastPrinted = new Date();

    // Create main itinerary worksheet
    const worksheet = workbook.addWorksheet('Trip Itinerary');

    // Define columns FIRST (4 columns: Day, Date, Activities, Accommodation)
    // This must be done before setting title to avoid overwriting
    const columns = [
      { header: 'Day', key: 'dayNumber', width: 8 },
      { header: 'Date', key: 'date', width: 20 },
      { header: 'Activities', key: 'activities', width: 60 },
      { header: 'Accommodation', key: 'accommodation', width: 40 }
    ];

    worksheet.columns = columns;

    // Add title AFTER columns are defined
    const titleRow = worksheet.getRow(1);
    titleRow.getCell(1).value = `${metadata?.tripName || 'Trip'} - Itinerary`;
    titleRow.getCell(1).style = this.defaultStyles.title;
    worksheet.mergeCells(1, 1, 1, 4);
    titleRow.height = 25;

    // Add date range and day count subtitle
    const subtitleRow = worksheet.getRow(2);
    if (metadata?.dateRange && metadata?.dayCount) {
      subtitleRow.getCell(1).value = `${metadata.dateRange} (${metadata.dayCount} day${metadata.dayCount !== 1 ? 's' : ''})`;
      subtitleRow.getCell(1).font = { italic: true, size: 11 };
      subtitleRow.getCell(1).alignment = { horizontal: 'center' };
      worksheet.mergeCells(2, 1, 2, 4);
    }

    // Add empty row
    worksheet.getRow(3);

    // Style header row (row 4)
    const headerRow = worksheet.getRow(4);
    columns.forEach((col, index) => {
      const cell = headerRow.getCell(index + 1);
      cell.value = col.header;

      // Apply styling WITHOUT borders - explicitly set each property
      cell.font = this.defaultStyles.header.font;
      cell.fill = this.defaultStyles.header.fill;
      cell.alignment = this.defaultStyles.header.alignment;

      // EXPLICITLY remove all borders
      cell.border = {};
    });
    headerRow.height = 20;

    // Add data rows
    let rowIndex = 5;
    itineraryData.forEach((day, index) => {
      const row = worksheet.getRow(rowIndex + index);

      // Day number (centered)
      row.getCell(1).value = day.dayNumber || (index + 1);
      row.getCell(1).alignment = { horizontal: 'center', vertical: 'top' };

      // Date
      row.getCell(2).value = day.date;

      // Activities (with line breaks and left alignment)
      row.getCell(3).value = Array.isArray(day.activities) ? day.activities.join('; ') : day.activities;
      row.getCell(3).alignment = {
        vertical: 'top',
        horizontal: 'left',
        wrapText: true,
        indent: 1
      };

      // Accommodation (with line breaks)
      row.getCell(4).value = day.accommodation || '';
      row.getCell(4).alignment = {
        vertical: 'top',
        horizontal: 'left',
        wrapText: true,
        indent: 1
      };

      // Apply cell styling WITHOUT using cell.style
      for (let col = 1; col <= 4; col++) {
        const cell = row.getCell(col);
        cell.font = this.defaultStyles.cell.font;
        cell.alignment = this.defaultStyles.cell.alignment;
        // Explicitly ensure no border
        cell.border = {};
      }

      // Override alignment for specific columns
      row.getCell(1).alignment = { horizontal: 'center', vertical: 'top' };
      row.getCell(3).alignment = {
        vertical: 'top',
        horizontal: 'left',
        wrapText: true,
        indent: 1
      };
      row.getCell(4).alignment = {
        vertical: 'top',
        horizontal: 'left',
        wrapText: true,
        indent: 1
      };

      // Calculate row height based on newline count in multi-line columns
      // Count newlines in Activities column (column 3)
      const activitiesText = row.getCell(3).value || '';
      const activityLines = (String(activitiesText).match(/\n/g) || []).length + 1;

      // Count newlines in Accommodation column (column 4)
      const accommodationText = row.getCell(4).value || '';
      const accommodationLines = (String(accommodationText).match(/\n/g) || []).length + 1;

      // Use the maximum line count from any column
      const maxLines = Math.max(activityLines, accommodationLines);

      // Set height: 15 points per line + 5 padding
      row.height = (maxLines * 15) + 5;
    });

    // NO AUTO-FILTER - Remove sort dropdown arrows
    // (Commented out the autoFilter configuration)
    // worksheet.autoFilter = {
    //   from: { row: 4, column: 1 },
    //   to: { row: rowIndex + itineraryData.length - 1, column: 3 }
    // };

    // Freeze panes
    worksheet.views = [
      { state: 'frozen', ySplit: 4 }
    ];

    // Generate buffer and return as blob
    const buffer = await workbook.xlsx.writeBuffer();
    return new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
  }

  /**
   * Generate Excel file for trip summary data
   *
   * @param {Object} tripData - Complete trip data
   * @returns {Promise<Blob>} Excel file as blob
   */
  async generateTripSummaryExcel(tripData) {
    const workbook = new ExcelJS.Workbook();

    workbook.creator = 'Trip Planner';
    workbook.created = new Date();

    // Overview worksheet
    const overviewWS = workbook.addWorksheet('Trip Overview');
    this.createTripOverviewSheet(overviewWS, tripData);

    // Days worksheet
    if (tripData.days && tripData.days.length > 0) {
      const daysWS = workbook.addWorksheet('Itinerary');
      this.createItinerarySheet(daysWS, tripData.days, tripData.trip);
    }

    // Places worksheet
    if (tripData.places && tripData.places.length > 0) {
      const placesWS = workbook.addWorksheet('Places');
      this.createPlacesSheet(placesWS, tripData.places);
    }

    // Accommodation worksheet
    if (tripData.accommodation && tripData.accommodation.length > 0) {
      const accomWS = workbook.addWorksheet('Accommodation');
      this.createAccommodationSheet(accomWS, tripData.accommodation);
    }

    // Travel worksheet
    if (tripData.travel && tripData.travel.length > 0) {
      const travelWS = workbook.addWorksheet('Travel');
      this.createTravelSheet(travelWS, tripData.travel);
    }

    const buffer = await workbook.xlsx.writeBuffer();
    return new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
  }

  /**
   * Create trip overview sheet
   */
  createTripOverviewSheet(worksheet, tripData) {
    worksheet.columns = [
      { header: 'Property', key: 'property', width: 20 },
      { header: 'Value', key: 'value', width: 40 }
    ];

    const trip = tripData.trip || {};
    const overview = [
      ['Trip Name', trip.name],
      ['Destination', trip.destination],
      ['Start Date', trip.startDate],
      ['End Date', trip.endDate],
      ['Duration', this.calculateDuration(trip.startDate, trip.endDate)],
      ['Travelers', trip.travelers],
      ['Budget', `${trip.budget} ${trip.currency}`],
      ['Status', trip.status],
      ['', ''],
      ['Data Summary', ''],
      ['Days', tripData.days?.length || 0],
      ['Places', tripData.places?.length || 0],
      ['Accommodations', tripData.accommodation?.length || 0],
      ['Travel Bookings', tripData.travel?.length || 0],
      ['Documents', tripData.documents?.length || 0]
    ];

    overview.forEach((item, index) => {
      const row = worksheet.getRow(index + 1);
      row.getCell(1).value = item[0];
      row.getCell(2).value = item[1];

      if (item[0] === 'Data Summary' || item[0] === '') {
        row.getCell(1).font = { bold: true };
      }
    });
  }

  /**
   * Create itinerary sheet
   */
  createItinerarySheet(worksheet, days, tripInfo) {
    const columns = [
      { header: 'Day #', key: 'dayNumber', width: 8 },
      { header: 'Date', key: 'date', width: 12 },
      { header: 'Title', key: 'title', width: 20 },
      { header: 'Activities', key: 'activities', width: 40 },
      { header: 'Notes', key: 'notes', width: 30 },
      { header: 'Budget', key: 'budget', width: 12 }
    ];

    worksheet.columns = columns;

    // Header styling
    const headerRow = worksheet.getRow(1);
    columns.forEach((col, index) => {
      const cell = headerRow.getCell(index + 1);
      cell.style = this.defaultStyles.header;
    });

    // Data rows
    days.forEach((day, index) => {
      const row = worksheet.getRow(index + 2);
      row.getCell(1).value = day.dayNumber || (index + 1);
      row.getCell(2).value = day.date;
      row.getCell(3).value = day.title;
      row.getCell(4).value = Array.isArray(day.activities) ? day.activities.join('; ') : day.activities;
      row.getCell(5).value = day.notes;
      row.getCell(6).value = day.budget;

      // Apply cell styling
      for (let col = 1; col <= 6; col++) {
        row.getCell(col).style = this.defaultStyles.cell;
      }
    });
  }

  /**
   * Create places sheet
   */
  createPlacesSheet(worksheet, places) {
    worksheet.columns = [
      { header: 'Name', key: 'name', width: 25 },
      { header: 'Type', key: 'type', width: 15 },
      { header: 'Address', key: 'address', width: 40 },
      { header: 'Visit Date', key: 'visitDate', width: 12 },
      { header: 'Duration', key: 'duration', width: 12 },
      { header: 'Notes', key: 'notes', width: 30 }
    ];

    this.addDataToSheet(worksheet, places);
  }

  /**
   * Create accommodation sheet
   */
  createAccommodationSheet(worksheet, accommodation) {
    worksheet.columns = [
      { header: 'Name', key: 'name', width: 25 },
      { header: 'Type', key: 'type', width: 15 },
      { header: 'Address', key: 'address', width: 30 },
      { header: 'Check In', key: 'checkIn', width: 12 },
      { header: 'Check Out', key: 'checkOut', width: 12 },
      { header: 'Nights', key: 'nights', width: 8 },
      { header: 'Guests', key: 'guests', width: 8 },
      { header: 'Room Type', key: 'roomType', width: 20 },
      { header: 'Total Cost', key: 'totalCost', width: 12 },
      { header: 'Confirmation', key: 'confirmationNumber', width: 15 }
    ];

    this.addDataToSheet(worksheet, accommodation);
  }

  /**
   * Create travel sheet
   */
  createTravelSheet(worksheet, travel) {
    worksheet.columns = [
      { header: 'Type', key: 'type', width: 12 },
      { header: 'Details', key: 'details', width: 20 },
      { header: 'Departure', key: 'departure', width: 25 },
      { header: 'Arrival', key: 'arrival', width: 25 },
      { header: 'Duration', key: 'duration', width: 12 },
      { header: 'Seat/Cabin', key: 'seat', width: 12 },
      { header: 'Cost', key: 'cost', width: 12 },
      { header: 'Booking Ref', key: 'bookingReference', width: 15 }
    ];

    // Transform travel data for Excel
    const travelData = travel.map(trip => ({
      type: trip.type,
      details: trip.airline ? `${trip.airline} ${trip.flightNumber}` : trip.operator || '',
      departure: trip.departure ? `${trip.departure.city} ${trip.departure.date} ${trip.departure.time}` : '',
      arrival: trip.arrival ? `${trip.arrival.city} ${trip.arrival.date} ${trip.arrival.time}` : '',
      duration: trip.duration,
      seat: trip.seatNumber || trip.cabinClass || '',
      cost: trip.cost ? `${trip.cost} ${trip.currency}` : '',
      bookingReference: trip.bookingReference || trip.confirmationNumber
    }));

    this.addDataToSheet(worksheet, travelData);
  }

  /**
   * Helper to add data to worksheet with styling
   */
  addDataToSheet(worksheet, data) {
    const columns = worksheet.columns;

    // Header styling
    const headerRow = worksheet.getRow(1);
    columns.forEach((col, index) => {
      const cell = headerRow.getCell(index + 1);
      cell.style = this.defaultStyles.header;
    });

    // Data rows
    data.forEach((item, index) => {
      const row = worksheet.getRow(index + 2);
      columns.forEach((col, colIndex) => {
        row.getCell(colIndex + 1).value = item[col.key] || '';
        row.getCell(colIndex + 1).style = this.defaultStyles.cell;
      });
    });

    // Auto-filter
    if (data.length > 0) {
      worksheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: data.length + 1, column: columns.length }
      };
    }
  }

  /**
   * Calculate duration between dates
   */
  calculateDuration(startDate, endDate) {
    if (!startDate || !endDate) return 'N/A';

    const start = new Date(startDate);
    const end = new Date(endDate);
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;

    return `${days} day${days !== 1 ? 's' : ''}`;
  }

  /**
   * Download Excel file
   *
   * @param {Blob} blob - Excel file blob
   * @param {string} filename - Filename without extension
   */
  downloadExcelFile(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }
}

export default new ExcelFormatGenerator();